import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleData } from '@/mocks/articles';
import { WebPageSchema, BreadcrumbListSchema, ArticleSchema, FAQPageSchema } from '@/components/base/SchemaOrg';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleData[slug as keyof typeof articleData] : null;

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-background-50 pt-20">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900">文章不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleUrl = `http://cgbast.cn/article/${article.slug}`;
  const breadcrumbItems = [
    { name: '首页', url: 'http://cgbast.cn/' },
    { name: article.category, url: 'http://cgbast.cn/' },
    { name: article.title, url: articleUrl },
  ];

  return (
    <>
      <WebPageSchema
        title={`${article.title} - 每日大赛黑料网`}
        description={article.subtitle}
        url={articleUrl}
      />
      <BreadcrumbListSchema items={breadcrumbItems} />
      <ArticleSchema
        title={article.title}
        description={article.subtitle}
        datePublished={article.date}
        author={article.author}
        imageUrl={article.imageUrl}
        url={articleUrl}
      />
      {article.faqItems && article.faqItems.length > 0 && (
        <FAQPageSchema questions={article.faqItems} />
      )}
      <Navbar />
      <main>
        {/* Dark Hero Section */}
        <section className="relative min-h-[80vh] flex flex-col items-center justify-center bg-foreground-950 overflow-hidden">
          {/* Subtle background texture */}
          <div className="absolute inset-0 opacity-10">
            <img
              src={article.imageUrl}
              alt=""
              className="w-full h-full object-cover blur-xl scale-110"
            />
          </div>

          <div className="relative z-10 w-full max-w-[900px] mx-auto px-6 flex flex-col items-center text-center pt-24 pb-16">
            {/* Category tag */}
            <span className="text-foreground-400 text-xs tracking-widest uppercase font-medium mb-6">
              {article.category}
            </span>

            {/* Title */}
            <h1 className="font-heading text-background-50 font-black text-2xl md:text-4xl lg:text-5xl leading-tight max-w-3xl">
              {article.title}
            </h1>
            <p className="text-foreground-400 text-base md:text-lg mt-5 max-w-2xl leading-relaxed">
              {article.subtitle}
            </p>

            {/* Meta */}
            <div className="flex items-center gap-6 mt-8 text-foreground-500 text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
              </span>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-4 mt-10">
              <button className="px-6 py-2.5 bg-background-50 text-foreground-900 font-medium text-sm rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-share-forward-line mr-1.5"></i>
                分享爆料
              </button>
              <button className="px-6 py-2.5 border border-foreground-700 text-background-50 font-medium text-sm rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-bookmark-line mr-1.5"></i>
                收藏
              </button>
            </div>
          </div>

          {/* Feature cards */}
          <div className="relative z-10 w-full max-w-[900px] mx-auto px-6 pb-16">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {['一手爆料', '每日更新', '真实可靠', '深度分析', '独家视角'].map((feat) => (
                <div
                  key={feat}
                  className="bg-foreground-800/60 rounded-xl px-4 py-3 flex items-center gap-2.5"
                >
                  <i className="ri-check-line text-primary-400 text-sm"></i>
                  <span className="text-foreground-300 text-xs whitespace-nowrap">{feat}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Breadcrumb + Content */}
        <section className="py-8 bg-background-50">
          <div className="max-w-[800px] mx-auto px-6 md:px-10">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
                {article.category}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 truncate max-w-[200px]">{article.title}</span>
            </nav>
          </div>
        </section>

        {/* Article content with evidence grid */}
        <section className="pb-12 md:pb-20 bg-background-50">
          <div className="max-w-[800px] mx-auto px-6 md:px-10">
            <article className="prose-custom">
              {article.content.map((block, idx) => {
                if (block.type === 'paragraph') {
                  return (
                    <p key={idx} className="text-foreground-700 leading-relaxed mb-6 text-base">
                      {block.text}
                    </p>
                  );
                }
                if (block.type === 'heading') {
                  return (
                    <h2 key={idx} className="text-foreground-900 font-bold text-xl md:text-2xl font-heading mt-10 mb-4">
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === 'image' && block.url) {
                  return (
                    <figure key={idx} className="my-8">
                      <div className="rounded-2xl overflow-hidden">
                        <img
                          src={block.url}
                          alt={block.caption || ''}
                          className="w-full object-cover"
                        />
                      </div>
                      {block.caption && (
                        <figcaption className="text-foreground-400 text-xs text-center mt-3">
                          {block.caption}
                        </figcaption>
                      )}
                    </figure>
                  );
                }
                if (block.type === 'quote') {
                  return (
                    <blockquote key={idx} className="border-l-4 border-primary-400 pl-5 py-2 my-8 bg-primary-50/50 rounded-r-xl">
                      <p className="text-foreground-700 text-lg italic font-heading leading-relaxed">
                        "{block.text}"
                      </p>
                    </blockquote>
                  );
                }
                return null;
              })}

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mt-12 pt-8 border-t border-background-200/70">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </article>
          </div>
        </section>

        {/* FAQ Section */}
        {article.faqItems && article.faqItems.length > 0 && (
          <section className="py-12 md:py-20 bg-background-100">
            <div className="max-w-[800px] mx-auto px-6 md:px-10">
              <h2 className="text-2xl md:text-3xl font-black text-foreground-900 font-heading mb-8 text-center">
                常见<span className="text-primary-500">问题</span>
              </h2>
              <div className="space-y-3">
                {article.faqItems.map((faq, idx) => (
                  <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/70">
                    <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                      <span className="text-foreground-900 font-medium text-sm pr-4">{faq.question}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform text-lg flex-shrink-0"></i>
                  </summary>
                    <div className="px-6 pb-4">
                      <p className="text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Related Articles */}
        <section className="py-12 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10">
            <h2 className="text-2xl md:text-3xl font-black text-foreground-900 font-heading mb-10">
              相关<span className="text-accent-500">爆料</span>
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
              {article.relatedArticles.map((related) => {
                const relatedArticle = articleData[related.slug as keyof typeof articleData];
                return (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group bg-background-50 rounded-2xl overflow-hidden cursor-pointer border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="aspect-[3/2] overflow-hidden">
                      {relatedArticle && (
                        <img
                          src={relatedArticle.imageUrl}
                          alt={related.title}
                          title={related.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="text-foreground-900 font-bold text-sm leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 md:py-20 bg-background-50">
          <div className="max-w-[800px] mx-auto px-6 md:px-10 text-center">
            <h2 className="text-2xl md:text-3xl font-black text-foreground-900 font-heading">
              不想错过<span className="italic font-light text-foreground-400">更多爆料？</span>
            </h2>
            <p className="text-foreground-500 text-sm mt-3 max-w-md mx-auto leading-relaxed">
              订阅每日大赛黑料网，第一时间获取网红塌房一手爆料
            </p>
            <a
              href="#subscribe"
              className="mt-6 inline-flex px-8 py-3 bg-foreground-900 text-background-50 font-semibold text-sm rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
            >
              立即订阅
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}